<template>
    <div class="modal_cont gsrbr020401">
        <div class="modal_header">
            {{ t.MainTitle }}
            <a href="#none" @click="closeModal" class="btn_close">{{ t.closeLabel }}</a>
        </div>

        <div class="modal_content">
            <div class="intro_section">
                <p class="greeting_text" v-html="t.Greeting"></p>
                <div class="desc_box">
                    <p class="desc_text" v-html="t.SubDesc"></p>
                </div>
            </div>

            <div class="consent_container">
                <div class="consent_header">
                    <h3 class="consent_title">{{ t.ConsentTitle }}</h3>
                    <ul class="bullet_list">
                        <li v-for="(item, idx) in t.ConsentItems" :key="idx">{{ item }}</li>
                    </ul>
                </div>
                <div class="consent_footer">
                    <p class="refuse_notice" v-html="t.RefuseNotice"></p>
                    <div class="agree_check">
                        <Inputs type="checkbox" :text="t.AgreeLabel" v-model="form.isAgree" />
                    </div>
                </div>
            </div>

            <div class="application_form">
                <div class="form_section">
                    <div class="section_title_row">
                        <h4 class="section_title">{{ t.SectionRegion }}</h4>
                        <span class="required_guide">{{ t.RequiredGuide }}</span>
                    </div>
                    <div class="input_rows">
                        <div class="form_row">
                            <label class="row_label">{{ t.LabelRegion }}<span class="ess">*</span></label>
                            <div class="row_content region_row">
                                <SelectBox :options="sidoOptions" v-model="form.regnCd" :initMsg="t.InitSido" @change="selectSidoComboChange" />
                                <SelectBox :options="sigunguOptions" v-model="form.borCd" :initMsg="t.InitSigungu" :is-disabled="!form.regnCd" />
                            </div>
                        </div>
                    </div>
                </div>

                <div class="form_section">
                    <div class="section_title_row">
                        <h4 class="section_title">{{ t.SectionCustomer }}</h4>
                        <span class="required_guide">{{ t.RequiredGuide }}</span>
                    </div>
                    <div class="input_rows">
                        <div class="form_row">
                            <label class="row_label">{{ t.LabelName }}<span class="ess">*</span></label>
                            <div class="row_content">
                                <Inputs type="text" v-model="form.wrtrNm" class="w205" maxlength="100" />
                            </div>
                        </div>
                        <div class="form_row">
                            <label class="row_label">{{ t.LabelEmail }}<span class="ess">*</span></label>
                            <div class="row_content email_row">
                                <Inputs
                                    type="text"
                                    v-model="form.wrtrEmailId"
                                    :maxlength="emailIdMaxLength"
                                />
                                <span class="unit">@</span>
                                <Inputs
                                    type="text"
                                    v-model="form.wrtrEmailDomain"
                                    :maxlength="emailDomainMaxLength"
                                />
                                <SelectBox :options="t.EmailOptions" v-model="form.wrtrEmailDomainSelect" class="w180" />
                            </div>
                        </div>
                        <div class="form_row">
                            <label class="row_label">{{ t.LabelPhone }}<span class="ess">*</span></label>
                            <div class="row_content phone_row">
                                <Inputs type="text" v-model="form.wrtrTelno1" class="w134" :is-readonly="true" />
                                <span class="unit">-</span>
                                <Inputs
                                    type="text"
                                    :model-value="form.wrtrTelno2"
                                    class="w134"
                                    inputmode="numeric"
                                    pattern="[0-9]*"
                                    maxlength="4"
                                    @beforeinput="preventNonDigitInput"
                                    @input="handlePhonePartInput($event, 'wrtrTelno2')"
                                    @update:modelValue="form.wrtrTelno2 = sanitizeFourDigitInput($event)"
                                />
                                <span class="unit">-</span>
                                <Inputs
                                    type="text"
                                    :model-value="form.wrtrTelno3"
                                    class="w134"
                                    inputmode="numeric"
                                    pattern="[0-9]*"
                                    maxlength="4"
                                    @beforeinput="preventNonDigitInput"
                                    @input="handlePhonePartInput($event, 'wrtrTelno3')"
                                    @update:modelValue="form.wrtrTelno3 = sanitizeFourDigitInput($event)"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div class="form_section">
                    <div class="section_title_row">
                        <h4 class="section_title">{{ t.SectionConsult }}</h4>
                        <span class="required_guide">{{ t.RequiredGuide }}</span>
                    </div>
                    <div class="input_rows">
                        <div class="form_row align_top">
                            <label class="row_label">{{ t.LabelAddress }}<span class="ess">*</span></label>
                            <div class="row_content address_row">
                                <div class="zip_code">
                                    <Inputs type="text" v-model="form.zipcd" :isDisabled="true" maxlength="6" />
                                    <Buttons type="button" class="btn_big border" @click="searchPost">{{ t.BtnZip }}</Buttons>
                                </div>
                                <div class="address_detail_group">
                                    <Inputs type="text" v-model="form.addr" :placeholder="t.PlaceholderAddrBasic" :isDisabled="true" maxlength="500" />
                                    <Inputs ref="dtlAddrInput" type="text" v-model="form.dtlAddr" :placeholder="t.PlaceholderAddrDetail" maxlength="500" />
                                </div>
                            </div>
                        </div>
                        <div class="form_row">
                            <label class="row_label">{{ t.LabelArea }}</label>
                            <div class="row_content area_row">
                                <div class="area_item">
                                    <span>{{ t.hp01 }}</span>
                                    <Inputs
                                        type="text"
                                        :model-value="form.strContExtent"
                                        class="w134"
                                        inputmode="numeric"
                                        pattern="[0-9]*"
                                        @beforeinput="preventNonDigitInput"
                                        @input="handleNumberInput($event, 'strContExtent')"
                                        @update:modelValue="form.strContExtent = sanitizeDigits($event)"
                                    />
                                    <span class="unit">m²</span>
                                </div>
                                <div class="area_item">
                                    <span>{{ t.hp02 }}</span>
                                    <Inputs
                                        type="text"
                                        :model-value="form.strExcluExtent"
                                        class="w134"
                                        inputmode="numeric"
                                        pattern="[0-9]*"
                                        @beforeinput="preventNonDigitInput"
                                        @input="handleNumberInput($event, 'strExcluExtent')"
                                        @update:modelValue="form.strExcluExtent = sanitizeDigits($event)"
                                    />
                                    <span class="unit">m²</span>
                                </div>
                                <span class="area_guide">{{ t.AreaGuide }}</span>
                            </div>
                        </div>
                        <div class="form_row">
                            <label class="row_label">{{ t.LabelStoreName }}</label>
                            <div class="row_content">
                                <Inputs type="text" v-model="form.conmNm" class="w347" maxlength="500" />
                            </div>
                        </div>
                        <div class="form_row">
                            <label class="row_label">{{ t.LabelFeature }}</label>
                            <div class="row_content w100">
                                <Textarea
                                    v-model="form.mkarChrcCntnt"
                                    name="inquiry_body"
                                    :placeholder="t.PlaceholderBody"
                                    :hint="t.HintBody"
                                    :rows="6"
                                    :maxlength="1000"
                                />
                            </div>
                        </div>
                        <div class="form_row">
                            <label class="row_label">{{ t.LabelRelation }}</label>
                            <div class="row_content">
                                <Inputs type="text" v-model="form.bldgOwnrRelCntnt" class="w347" maxlength="200" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="button_area">
                <Buttons type="button" class="btn_big primary" @click="submitForm">{{ t.BtnSubmit }}</Buttons>
                <Buttons type="button" class="btn_big gray" @click="resetForm">{{ t.BtnReset }}</Buttons>
            </div>

            <KakaoPostcode ref="postcode" @complete="handlePostcodeComplete" @error="handlePostcodeError" />
        </div>
    </div>
</template>

<script>
import modal from "@/utils/popupModal";
import Inputs from "@/components/Inputs.vue";
import SelectBox from "@/components/SelectBox.vue";
import Buttons from "@/components/Buttons.vue";
import Textarea from "@/components/Textarea.vue";
import KakaoPostcode from "@/components/KakaoPostcode.vue";
import { startupAPI } from "@/api/startupAPI.js";
import { gisAPI } from "@/api/gisAPI.js";
import { COMM_CD_GROUP_IDS, loadCommCdGroupItems } from "@/utils/CommonCode.js";

export default {
    name: "GsSiteProposalDetail",
    components: {
        Inputs,
        SelectBox,
        Buttons,
        Textarea,
        KakaoPostcode,
    },
    data() {
        return {
            page: 1,
            isSubmitting: false,
            form: this.createInitialForm(),
            sidoOptions: [],
            sigunguOptions: [],
            langData: {
                ko: {
                    closeLabel: "닫기",
                    MainTitle: "입지제안",
                    Greeting: "안녕하세요!<br/>GS THE FRESH 입지상담 담당자입니다.",
                    SubDesc: "아래의 상담 신청서를 작성하시면 귀하만의 상담을 받아 보실 수 있습니다.<br/>상담신청 시, 담당자 확인 후 연락드리겠습니다.",
                    ConsentTitle: "개인정보 수집·이용 동의",
                    ConsentItems: [
                        "입력하신 정보는 입지상담을 위해서만 사용합니다. 수집항목, 이용 및 목적, 보유 및 이용기간은 다음과 같으며,기타 개인정보 취급사항은 홈페이지 하단의 '개인정보 처리방침'을 참고하시기 바랍니다.",
                        "수집하는 개인정보 항목: 이름, 이메일, 휴대폰번호",
                        "수집이용 및 목적: 수집한 개인정보를 본인 식별 및 문의사항 확인 및 답변을 위해 활용",
                        "보유 및 이용기간: 접수 후 1년"
                    ],
                    RefuseNotice: "고객님께서는 본 동의에 거부하실 권리가 있으나, <br/>동의하지 않으실 경우 입지제안 신청 글 작성이 불가능합니다.",
                    AgreeLabel: "동의합니다.",
                    SectionRegion: "지역선택",
                    SectionCustomer: "고객정보",
                    SectionConsult: "상담내용",
                    RequiredGuide: "* 필수 입력사항",
                    LabelRegion: "지역",
                    LabelName: "이름", LabelEmail: "이메일", LabelPhone: "휴대폰",
                    LabelAddress: "추천점포 소재지", LabelArea: "추천점포 면적", LabelStoreName: "점포상호", LabelFeature: "상권특징", LabelRelation: "건물주와의 관계",
                    BtnZip: "우편번호 찾기", BtnSubmit: "상담신청", BtnReset: "다시작성",
                    hp01: "계약면적", hp02: "전용면적",
                    PlaceholderBody: "내용을 입력해 주세요", HintBody: "최대 500자까지 입력할 수 있습니다.", AlertAgree: "동의 항목에 체크해주세요.",
                    InitSido: "시/도 선택", InitSigungu: "구/군 선택",
                    PlaceholderAddrBasic: "기본주소", PlaceholderAddrDetail: "상세주소", PlaceholderRelation: "예: 본인 소유, 임차인 등",
                    AreaGuide: "* m² = (기준)평 x 3.3",
                    EmailOptions: [{ value: "", label: "직접입력" }, { value: "naver.com", label: "naver.com" }, { value: "gmail.com", label: "gmail.com" }],
                    PhoneOptions: [{ value: "010", label: "010" }, { value: "011", label: "011" }],
                    RelationOptions: [{ value: "본인", label: "본인 소유" }, { value: "임차인", label: "임차인" }],
                    StoreOptions: [{ value: "", label: "선택하세요" }, { value: "store01", label: "상호A" }],
                    FeatureOptions: [{ value: "", label: "선택하세요" }, { value: "feat01", label: "상권A" }]
                },
                en: {
                    closeLabel: "Close"/* 260708 번역 */,
                    MainTitle: "Location Inquiry",
                    Greeting: "Hello!<br/>I am a GS THE FRESH location consultation representative."/* 260604 번역 */,
                    SubDesc: "Fill out the consultation request form below to receive a consultation tailored just for you.<br/>Once you submit your consultation request, our representative will review it and contact you."/* 260604 번역 */,
                    ConsentTitle: "Consent to Collection and Use of Personal Information",
                    ConsentItems: [
                        "The information you provide is used solely for location consultation. The items collected, the use and purpose, and the retention and use period are as follows. For other matters regarding the handling of personal information, please refer to the 'Privacy Policy' at the bottom of the website."/* 260604 번역 */,
                        "Personal information items collected: name, email, mobile phone number"/* 260604 번역 */,
                        "Collection, Use, and Purpose: The collected personal information is used for identity verification and to verify and respond to inquiries."/* 260604 번역 */,
                        "- Retention and Use Period: One year after receipt"
                    ],
                    RefuseNotice: "You have the right to refuse this consent; however, <br/>If you do not agree, you will not be able to submit a location proposal application."/* 260604 번역 */,
                    AgreeLabel: "I agree.",
                    SectionCustomer: "Customer Information",
                    SectionConsult: "Consultation Content",
                    RequiredGuide: "* Required fields",
                    LabelName: "Name", LabelEmail: "Email", LabelPhone: "Mobile phone"/* 260604 번역 */,
                    LabelRegion: "Region",
                    LabelAddress: "Recommended store location"/* 260604 번역 */, LabelArea: "Recommended store area"/* 260604 번역 */, LabelStoreName: "Store name"/* 260604 번역 */, LabelFeature: "Commercial Area Characteristics"/* 260604 번역 */, LabelRelation: "Relationship with the Building Owner"/* 260604 번역 */,
                    BtnZip: "Find postal code"/* 260604 번역 */, BtnSubmit: "Consultation Request"/* 260604 번역 */, BtnReset: "Rewrite"/* 260604 번역 */,
                    hp01: "Contract Area"/* 260708 번역 */, hp02: "Exclusive Area"/* 260708 번역 */,
                    PlaceholderBody: "Please enter your content"/* 260708 번역 */, HintBody: "You can enter up to 500 characters."/* 260708 번역 */, AlertAgree: "Please check the consent items."/* 260708 번역 */,
                    InitSido: "Select region", InitSigungu: "Select district",
                    PlaceholderAddrBasic: "Base Address"/* 260604 번역 */, PlaceholderAddrDetail: "Detailed address"/* 260604 번역 */, PlaceholderRelation: "e.g., owner-occupied, tenant, etc."/* 260604 번역 */,
                    AreaGuide: "* m² = (standard) pyeong x 3.3"/* 260604 번역 */,
                    EmailOptions: [{ value: "", label: "Enter directly" }, { value: "naver.com", label: "naver.com" }, { value: "gmail.com", label: "gmail.com" }],
                    PhoneOptions: [{ value: "010", label: "010" }, { value: "011", label: "011" }],
                    RelationOptions: [{ value: "Self"/* 260604 번역 */, label: "Owned by Self"/* 260604 번역 */ }, { value: "Tenant"/* 260604 번역 */, label: "Tenant"/* 260604 번역 */ }],
                    StoreOptions: [{ value: "", label: "Please select"/* 260604 번역 */ }, { value: "store01", label: "Business Name A"/* 260604 번역 */ }],
                    FeatureOptions: [{ value: "", label: "Please select"/* 260604 번역 */ }, { value: "feat01", label: "Commercial Area A"/* 260604 번역 */ }]
                }
            }
        };
    },
    mounted() {
        this.loadOptions();
    },
    computed: {
        t() { return this.langData[(document.querySelector('.language button.current')?.textContent.trim().toLowerCase()||'ko')] || this.langData.ko; },
        emailIdMaxLength() {
            return Math.max(100 - this.form.wrtrEmailDomain.trim().length - 1, 0);
        },
        emailDomainMaxLength() {
            return Math.max(100 - this.form.wrtrEmailId.trim().length - 1, 0);
        }
    },
    watch: {
        "form.wrtrEmailDomainSelect"(value) {
            if (value) {
                this.form.wrtrEmailDomain = value;
            }
        },
        "form.wrtrEmailId"(value) {
            this.form.wrtrEmailId = this.limitString(value, this.emailIdMaxLength);
        },
        "form.wrtrEmailDomain"(value) {
            this.form.wrtrEmailDomain = this.limitString(value, this.emailDomainMaxLength);
        },
        "form.wrtrTelno1"() {
            this.form.wrtrTelno1 = "010";
        }
    },
    methods: {
        createInitialForm() {
            return {
                isAgree: false,
                rsrvDttm: "",
                wrtrNm: "",
                wrtrEmailId: "",
                wrtrEmailDomain: "",
                wrtrEmailDomainSelect: "",
                wrtrTelno1: "010",
                wrtrTelno2: "",
                wrtrTelno3: "",
                psnId: "",
                regnCd: "",
                borCd: "",
                zipcd: "",
                addr: "",
                dtlAddr: "",
                strContExtent: "",
                strExcluExtent: "",
                conmNm: "",
                mkarChrcCntnt: "",
                bldgOwnrRelCntnt: "",
                answrYn: "N",
                regUserId: "SYSTEM",
                gsfLocaProposConslAceptStatCd: "01"
            };
        },
        async loadOptions() {
            const codeItems = await loadCommCdGroupItems({
                sidoItems: COMM_CD_GROUP_IDS.GS25_BRFS_REGN_DV_CD
            });

            this.sidoOptions = codeItems.sidoItems.map(this.toSelectOption);
        },
        toSelectOption(item) {
            return {
                value: item.value,
                label: item.title,
            };
        },
        async selectSidoComboChange() {
            this.form.borCd = "";
            this.sigunguOptions = [];

            if (!this.form.regnCd) return;

            try {
                const response = await gisAPI.searchGungu({ sido: this.form.regnCd });
                if (response.data.resultCode === "00000") {
                    this.sigunguOptions = (response.data.result || []).map((gungu) => ({
                        value: gungu.gunguCd,
                        label: gungu.gunguNm,
                    }));
                }
            } catch (error) {
                return null;
            }
        },
        closeModal(event) { modal.close(event.currentTarget); },
        searchPost() {
            this.$refs.postcode.open();
        },
        handlePostcodeComplete(result) {
            this.form.zipcd = result.zonecode || "";
            this.form.addr = `${result.address || ""}${result.extraAddress || ""}`;
            this.form.dtlAddr = "";

            this.$nextTick(() => {
                const input = this.$refs.dtlAddrInput?.$el?.querySelector("input");

                if (input) {
                    input.focus();
                }
            });
        },
        handlePostcodeError(error) {
            console.error("Kakao postcode error:", error);
            alert("우편번호 서비스를 불러오지 못했습니다.");
        },
        async submitForm() {
            if (this.isSubmitting) return;
            if (!this.validateForm()) return;

            this.isSubmitting = true;

            try {
                await startupAPI.insertLocationProposal(this.createSubmitPayload());

                alert("상담신청이 접수되었습니다.");
                this.resetForm();
            } catch (error) {
                console.error("입지제안 저장 오류:", error);
                alert(error?.message || "저장 중 오류가 발생했습니다.");
            } finally {
                this.isSubmitting = false;
            }
        },
        resetForm() {
            this.form = this.createInitialForm();
            this.sigunguOptions = [];
        },
        validateForm() {
            this.normalizeForm();

            if (!this.form.isAgree) {
                alert(this.t.AlertAgree);
                return false;
            }

            if (!this.form.regnCd || !this.form.borCd) {
                alert("지역을 선택해주세요.");
                return false;
            }

            if (!this.form.wrtrNm.trim()) {
                alert("이름을 입력해주세요.");
                return false;
            }

            if (!this.form.wrtrEmailId.trim() || !this.form.wrtrEmailDomain.trim()) {
                alert("이메일을 입력해주세요.");
                return false;
            }

            if (!this.form.wrtrTelno1 || !this.form.wrtrTelno2.trim() || !this.form.wrtrTelno3.trim()) {
                alert("휴대폰 번호를 입력해주세요.");
                return false;
            }

            if (!/^\d{4}$/.test(this.form.wrtrTelno2) || !/^\d{4}$/.test(this.form.wrtrTelno3)) {
                alert("휴대폰 번호를 4자리 숫자로 입력해주세요.");
                return false;
            }

            return true;
        },
        createSubmitPayload() {
            this.normalizeForm();

            const wrtrEmailAddr = `${this.form.wrtrEmailId.trim()}@${this.form.wrtrEmailDomain.trim()}`;
            const wrtrTelno = [this.form.wrtrTelno1, this.form.wrtrTelno2, this.form.wrtrTelno3]
                .map((value) => String(value || "").trim())
                .join("-");

            return {
                psnId: this.form.psnId,
                rsrvDttm: this.form.rsrvDttm || /*this.getCurrentDateTime()*/ null,
                wrtrNm: this.form.wrtrNm.trim(),
                wrtrEmailAddr,
                wrtrTelno,
                gsfLocaProposConslAceptStatCd: this.form.gsfLocaProposConslAceptStatCd || null,
                regnCd: this.form.regnCd,
                borCd: this.form.borCd,
                zipcd: this.form.zipcd.trim(),
                addr: this.form.addr.trim(),
                dtlAddr: this.form.dtlAddr.trim(),
                strContExtent: this.toNumberOrNull(this.form.strContExtent),
                strExcluExtent: this.toNumberOrNull(this.form.strExcluExtent),
                conmNm: this.form.conmNm.trim(),
                mkarChrcCntnt: this.form.mkarChrcCntnt.trim(),
                bldgOwnrRelCntnt: this.form.bldgOwnrRelCntnt.trim(),
                answrYn: this.form.answrYn || "N",
                regUserId: this.form.regUserId,
            };
        },
        normalizeForm() {
            this.form.wrtrTelno1 = "010";
            this.form.wrtrTelno2 = this.sanitizeFourDigitInput(this.form.wrtrTelno2);
            this.form.wrtrTelno3 = this.sanitizeFourDigitInput(this.form.wrtrTelno3);
            this.form.strContExtent = this.sanitizeDigits(this.form.strContExtent);
            this.form.strExcluExtent = this.sanitizeDigits(this.form.strExcluExtent);
            this.form.wrtrNm = this.limitString(this.form.wrtrNm, 100);
            this.form.wrtrEmailId = this.limitString(this.form.wrtrEmailId, this.emailIdMaxLength);
            this.form.wrtrEmailDomain = this.limitString(this.form.wrtrEmailDomain, this.emailDomainMaxLength);
            this.form.zipcd = this.limitString(this.form.zipcd, 6);
            this.form.regnCd = this.limitString(this.form.regnCd, 20);
            this.form.borCd = this.limitString(this.form.borCd, 20);
            this.form.addr = this.limitString(this.form.addr, 500);
            this.form.dtlAddr = this.limitString(this.form.dtlAddr, 500);
            this.form.conmNm = this.limitString(this.form.conmNm, 500);
            this.form.mkarChrcCntnt = this.limitString(this.form.mkarChrcCntnt, 1000);
            this.form.bldgOwnrRelCntnt = this.limitString(this.form.bldgOwnrRelCntnt, 200);
            this.form.answrYn = this.limitString(this.form.answrYn || "N", 1);
            this.form.psnId = this.limitString(this.form.psnId || "HOMEPAGE", 20);
            this.form.regUserId = this.limitString(this.form.regUserId || "HOMEPAGE", 20);
            this.form.gsfLocaProposConslAceptStatCd = this.limitString(this.form.gsfLocaProposConslAceptStatCd, 2);
        },
        sanitizeFourDigitInput(value) {
            return this.sanitizeDigits(value).slice(0, 4);
        },
        sanitizeDigits(value) {
            return String(value ?? "").replace(/\D/g, "");
        },
        preventNonDigitInput(event) {
            if (event.inputType?.startsWith("delete") || !event.data) return;
            if (/\D/.test(event.data)) event.preventDefault();
        },
        handlePhonePartInput(event, fieldName) {
            const value = this.sanitizeFourDigitInput(event.target.value);
            event.target.value = value;
            this.form[fieldName] = value;
        },
        handleNumberInput(event, fieldName) {
            const value = this.sanitizeDigits(event.target.value);
            event.target.value = value;
            this.form[fieldName] = value;
        },
        limitString(value, maxLength) {
            return String(value ?? "").slice(0, maxLength);
        },
        getCurrentDateTime() {
            const date = new Date();
            const pad = (value) => String(value).padStart(2, "0");

            return [
                date.getFullYear(),
                pad(date.getMonth() + 1),
                pad(date.getDate())
            ].join("-") + " " + [
                pad(date.getHours()),
                pad(date.getMinutes()),
                pad(date.getSeconds())
            ].join(":");
        },
        toNumberOrNull(value) {
            if (value === "" || value === null || value === undefined) return null;

            const numberValue = Number(String(value).replace(/,/g, ""));
            return Number.isNaN(numberValue) ? null : numberValue;
        }
    }
};
</script>

<style scoped>
.modal_header { flex-shrink: 0; display: flex; align-items: center; justify-content: space-between; padding-bottom: 40px; font-family: "Pretendard", Helvetica; font-weight: 700; font-size: 40px; color: #161616; border-bottom: 0; }
.modal_content { flex: 1; overflow-y: auto; padding-right: 10px; }
.intro_section { margin-bottom: 40px; display: flex; flex-direction: column; gap: 16px; }
.greeting_text { font-family: "Pretendard", Helvetica; font-weight: 700; font-size: 32px; color: #161616; line-height: 1.3; }
.desc_text { font-family: "Pretendard", Helvetica; font-size: 1.8rem; line-height: 1.4; color: #161616; }
.consent_container { padding: 32px; background-color: #f8f8f8; border-radius: 12px; margin-bottom: 40px; }
.consent_title { font-size: 24px; font-weight: 700; color: #161616; margin-bottom: 24px; }
.bullet_list { display: flex; flex-direction: column; gap: 8px; margin-bottom: 24px; }
.bullet_list li { position: relative; padding-left: 12px; font-size: 16px; color: #67676f; line-height: 1.5; }
.bullet_list li::before { content: ''; position: absolute; left: 0; top: 10px; width: 4px; height: 1px; background: #67676f; }
.consent_footer { display: flex; align-items: center; justify-content: space-between; padding-top: 24px; border-top: 1px solid #d7d7df; }
.refuse_notice { font-size: 16px; font-weight: 700; color: #161616; line-height: 1.2; }
.application_form { display: flex; flex-direction: column; gap: 64px; }
.form_section { display: flex; flex-direction: column; }
.section_title_row { display: flex; align-items: center; justify-content: space-between; padding-bottom: 16px; border-bottom: 1px solid #242428; }
.section_title { font-size: 24px; font-weight: 700; color: #161616; }
.required_guide { font-size: 14px; color: #fb6432; }
.input_rows { display: flex; flex-direction: column; padding: 24px 0; border-bottom: 1px solid #e5e5e9; gap: 24px; }
.form_row { display: flex; align-items: center; }
.form_row.align_top { align-items: flex-start; }
/* 🔥 PC 라벨 정렬: 입력창 시작 지점 일치 */
.row_label { width: 160px; flex-shrink: 0; font-size: 16px; color: #161616; display: flex; align-items: center; gap: 4px; }
.ess { color: #fb6432; }
.row_content {display: flex; align-items: center; gap: 8px; }
.email_row, .phone_row { display: flex; align-items: center; gap: 8px; }
.region_row :deep(.select) { width: 220px; }
.region_row :deep(.select select) { width: 100%; }
.unit { color: #67676f; font-size: 16px; }
.address_row {width:100%; flex-direction: column; align-items: flex-start; gap: 8px; }
.zip_code { display: flex; gap: 8px; }
.area_row { display: flex; align-items: center; gap: 24px;}
.area_item { display: flex; align-items: center; gap: 8px; }
.area_guide { font-size: 14px; color: #107af2; }
.button_area { display: flex; justify-content: flex-end; gap: 8px; margin-top: 40px; }
.w205 { width: 205px; } .w180 { width: 180px; } .w134 { width: 134px; } .w347 { width: 347px; } .w100 { width: 100%; }
.btn_big.border {height:auto !important;}
/* 🔥 PC: 주소 입력 필드 5:5 배치를 위한 스타일 */
.address_detail_group { display: flex; gap: 8px; width: 100%; }
.address_detail_group :deep(.input_wrap) { flex: 1; }

/* 태블릿 반응형 */
@media screen and (max-width: 1024px) {
    .modal_cont.gsrbr020401 {
        padding: 0;
    }
    .consent_footer {
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
    }
    .form_row {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }
    .row_label {
        width: 100%;
    }
    .row_content {
        width: 100%;
    }
    .w205, .w180, .w134, .w347 {
        width: 100%;
    }
    .area_row {
        gap: 12px;
    }
    .region_row :deep(.select) {
        flex: 1;
        width: 100%;
    }
}

/* 모바일 반응형: 767px 이하 한 줄 배치 */
@media screen and (max-width: 767px) {
    .consent_container {
        padding: 20px;
    }
    .email_row, .phone_row {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
        width: 100%;
    }
    .email_row :deep(.input_wrap), 
    .phone_row :deep(.input_wrap), 
    .phone_row :deep(.select_wrap),
    .region_row :deep(.select),
    .address_row :deep(.input_wrap) {
        width: 100% !important;
    }
    .unit {
        display: none;
    }
    .area_item {
        width: 100%;
        justify-content: space-between;
    }
    .area_item .unit {
        display: block;
    }
    .address_detail_group {
        flex-direction: column;
        width: 100%;
        gap: 12px;
    }
    .address_detail_group :deep(.input_wrap) {
        width: 100% !important;
    }
    .area_row {flex-wrap:wrap;}
}
</style>
